"use client";

import React, { useState } from "react";
import {
  Edit,
  Trash2,
  Copy,
  ExternalLink,
  Share2,
  Calendar,
  Loader2,
  AlertCircle,
  Settings,
  ShieldAlert,
} from "lucide-react";
import { Button } from "../button.js";
import { Badge } from "../badge.js";
import { useToast } from "../../hooks/use-toast.js";
import { CalendarEvent } from "../../types/calendar.js";
import { format } from "date-fns";
import {
  useUpdateGoogleEvent,
  useDeleteGoogleEvent,
  useUpdateMicrosoftEvent,
  useDeleteMicrosoftEvent
} from "../../hooks/use-event-mutations.js";
import { EventFormSection } from "./calendarinvites/shared/EventFormShell.js";

interface EventActionsTabProps {
  event: CalendarEvent;
  onEdit?: (event: CalendarEvent) => void;
  onDelete?: (event: CalendarEvent) => void;
  onClose?: () => void;
  isPageView?: boolean;
}

export function EventActionsTab({
  event,
  onEdit,
  onDelete,
  onClose,
}: EventActionsTabProps) {
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [actionStates, setActionStates] = useState<{
    copying: boolean;
    sharing: boolean;
    opening: boolean;
  }>({
    copying: false,
    sharing: false,
    opening: false
  });

  // Event mutation hooks
  const updateGoogleEvent = useUpdateGoogleEvent();
  const deleteGoogleEvent = useDeleteGoogleEvent();
  const updateMicrosoftEvent = useUpdateMicrosoftEvent();
  const deleteMicrosoftEvent = useDeleteMicrosoftEvent();

  const isGoogleEvent = event.source === "google";
  const isMicrosoftEvent = event.source === "microsoft";

  const handleCopyToClipboard = async (text: string) => {
    setActionStates(prev => ({ ...prev, copying: true }));
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied to clipboard",
        description: "Event details copied successfully",
      });
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Could not copy to clipboard",
        variant: "destructive",
      });
    } finally {
      setActionStates(prev => ({ ...prev, copying: false }));
    }
  };

  const handleCopyEventDetails = () => {
    const startDate = new Date(event.start);
    const endDate = new Date(event.end);
    const startTime = format(startDate, "p");
    const endTime = format(endDate, "p");
    const date = format(startDate, "PPP");

    const eventDetails = `${event.title}\n${date} • ${startTime} - ${endTime}${event.location ? `\n${event.location}` : ""}${event.description ? `\n\n${event.description}` : ""}`;

    handleCopyToClipboard(eventDetails);
  };

  const handleOpenInCalendar = () => {
    setActionStates(prev => ({ ...prev, opening: true }));
    try {
      if (event.webLink) {
        window.open(event.webLink, "_blank");
      } else {
        toast({
          title: "No calendar link available",
          description: "This event doesn't have a direct calendar link",
          variant: "destructive",
        });
      }
    } finally {
      setActionStates(prev => ({ ...prev, opening: false }));
    }
  };

  const handleShareEvent = async () => {
    setActionStates(prev => ({ ...prev, sharing: true }));
    try {
      const eventUrl = event.webLink || window.location.href;

      if (navigator.share) {
        try {
          await navigator.share({
            title: event.title,
            text: `Check out this event: ${event.title}`,
            url: eventUrl,
          });
        } catch (error) {
          // User cancelled sharing or error occurred
          console.log("Share cancelled or failed:", error);
        }
      } else {
        // Fallback to copying the link
        handleCopyToClipboard(eventUrl);
      }
    } finally {
      setActionStates(prev => ({ ...prev, sharing: false }));
    }
  };

  const handleDeleteEvent = async () => {
    if (!event.id) {
      setDeleteError("Cannot delete event: No event ID found");
      toast({
        title: "Error",
        description: "Cannot delete event: No event ID found",
        variant: "destructive",
      });

      return;
    }

    setIsDeleting(true);
    setDeleteError(null);

    try {
      if (isGoogleEvent) {
        await deleteGoogleEvent.mutateAsync({ eventId: event.id });
      } else if (isMicrosoftEvent) {
        await deleteMicrosoftEvent.mutateAsync({ eventId: event.id });
      } else {
        throw new Error("Unknown event source");
      }

      onDelete?.(event);
      onClose?.();
    } catch (error) {
      console.error("Failed to delete event:", error);
      const errorMessage = error instanceof Error ? error.message : "An error occurred";
      setDeleteError(errorMessage);
      toast({
        title: "Error",
        description: "Failed to delete event. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditEvent = () => {
    onEdit?.(event);
  };

  const primaryActions = [
    {
      label: "Copy Details",
      icon: actionStates.copying ? Loader2 : Copy,
      onClick: handleCopyEventDetails,
      variant: "outline" as const,
      disabled: actionStates.copying,
      description: "Copy event details to clipboard"
    },
    {
      label: "Open in Calendar",
      icon: actionStates.opening ? Loader2 : ExternalLink,
      onClick: handleOpenInCalendar,
      variant: "outline" as const,
      disabled: !event.webLink || actionStates.opening,
      description: "Open event in your calendar app"
    },
    {
      label: "Share Event",
      icon: actionStates.sharing ? Loader2 : Share2,
      onClick: handleShareEvent,
      variant: "outline" as const,
      disabled: actionStates.sharing,
      description: "Share event with others"
    },
  ];

  const destructiveActions = [
    {
      label: "Edit Event",
      icon: Edit,
      onClick: handleEditEvent,
      disabled: !onEdit,
      description: "Edit event details"
    },
    {
      label: isDeleting ? "Deleting..." : "Delete Event",
      icon: isDeleting ? Loader2 : Trash2,
      onClick: handleDeleteEvent,
      disabled: !onDelete || isDeleting,
      destructive: true,
      description: "Delete this event permanently"
    },
  ];

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {deleteError && (
        <div className="flex items-center space-x-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <span className="text-sm text-destructive">{deleteError}</span>
        </div>
      )}

      {/* Primary Actions */}
      <EventFormSection title="Quick actions" icon={Settings}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-2">
          {primaryActions.map((action, index) => (
            <Button
              key={index}
              type="button"
              variant={action.variant}
              size="sm"
              onClick={action.onClick}
              disabled={action.disabled}
              className="flex h-auto flex-col items-center space-y-2 border-orange-200 py-4 text-orange-700 hover:bg-orange-50"
            >
              <action.icon className="h-5 w-5" />
              <div className="text-center">
                <div className="text-sm font-medium">{action.label}</div>
                <div className="mt-1 text-xs text-orange-600">
                  {action.description}
                </div>
              </div>
            </Button>
          ))}
        </div>
      </EventFormSection>

      {/* Destructive Actions */}
      <EventFormSection title="Event management" icon={ShieldAlert}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {destructiveActions.map((action, index) => (
            <Button
              key={index}
              type="button"
              variant={action.destructive ? "destructive" : "outline"}
              size="sm"
              onClick={action.onClick}
              disabled={action.disabled}
              className="flex h-auto flex-col items-center space-y-2 py-4"
            >
              <action.icon className="h-5 w-5" />
              <div className="text-center">
                <div className="text-sm font-medium">{action.label}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {action.description}
                </div>
              </div>
            </Button>
          ))}
        </div>
      </EventFormSection>

      {/* Event Information */}
      <EventFormSection title="Event information" icon={Calendar}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
          <div className="rounded-lg bg-muted/30 p-4">
            <div className="mb-2 flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Source</span>
            </div>
            <Badge className="capitalize">
              {event.source}
            </Badge>
          </div>

          <div className="rounded-lg bg-muted/30 p-4">
            <div className="mb-2 flex items-center space-x-2">
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Event ID</span>
            </div>
            <p className="font-mono text-sm text-muted-foreground">
              {event.id ? `${event.id.slice(0, 8)}...` : 'No ID'}
            </p>
          </div>
        </div>
      </EventFormSection>
    </div>
  );
}
